import { useMemo, useState } from "react";
import type { Comment, Filters, StageKey, Task, ViewMode } from "./mock/types";
import { TASKS } from "./mock/data";
import { CURRENT_USER } from "./mock/session";
import { TasksV2Header } from "./components/TasksV2Header";
import { TasksV2Filters } from "./components/TasksV2Filters";
import { KanbanBoard } from "./components/KanbanBoard";
import { TaskTable } from "./components/TaskTable";
import { TaskDetailDrawer } from "./components/detail/TaskDetailDrawer";
import { CreateTaskDrawer } from "./components/detail/CreateTaskDrawer";
import { DrawerShell } from "./components/detail/DrawerShell";

const EMPTY_FILTERS: Filters = {
	projectId: "",
	sprintId: "",
	userId: "",
	status: "",
	search: "",
};

function matches(task: Task, f: Filters): boolean {
	if (f.projectId && task.project_id !== f.projectId) return false;
	if (f.sprintId && task.sprint_id !== f.sprintId) return false;
	if (f.status && task.status !== f.status) return false;
	if (f.userId) {
		const ids = [
			task.assigned_to?.id,
			...task.additional_assignees.map((p) => p.id),
		];
		if (!ids.includes(f.userId)) return false;
	}
	if (f.search) {
		const q = f.search.toLowerCase();
		const hay = `${task.title} ${task.ticket_code ?? ""}`.toLowerCase();
		if (!hay.includes(q)) return false;
	}
	return true;
}

export default function TasksV2Page() {
	const [tasks, setTasks] = useState<Task[]>(TASKS);
	const [view, setView] = useState<ViewMode>("board");
	const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [createOpen, setCreateOpen] = useState(false);
	const [createParentId, setCreateParentId] = useState<string | null>(null);

	const filtered = useMemo(
		() => tasks.filter((t) => matches(t, filters)),
		[tasks, filters],
	);

	const selectedTask = useMemo(
		() => tasks.find((t) => t.id === selectedId) ?? null,
		[tasks, selectedId],
	);

	const childTasks = useMemo(
		() =>
			selectedTask
				? tasks.filter((t) => t.parent_task_id === selectedTask.id)
				: [],
		[tasks, selectedTask],
	);

	const parentTask = useMemo(
		() =>
			selectedTask?.parent_task_id
				? tasks.find((t) => t.id === selectedTask.parent_task_id) ?? null
				: null,
		[tasks, selectedTask],
	);

	const createParent = useMemo(
		() =>
			createParentId
				? tasks.find((t) => t.id === createParentId) ?? null
				: null,
		[tasks, createParentId],
	);

	const openCreate = (parentId: string | null) => {
		setCreateParentId(parentId);
		setCreateOpen(true);
	};

	const patchTask = (id: string, patch: (t: Task) => Task) =>
		setTasks((prev) => prev.map((t) => (t.id === id ? patch(t) : t)));

	const toggleChecklist = (itemId: string) => {
		if (!selectedTask) return;
		patchTask(selectedTask.id, (t) => ({
			...t,
			checklist: t.checklist.map((c) =>
				c.id === itemId ? { ...c, done: !c.done } : c,
			),
		}));
	};

	const addComment = (body: string) => {
		if (!selectedTask) return;
		const comment: Comment = {
			id: `cm-${Date.now()}`,
			author: CURRENT_USER,
			body,
			created_at: new Date().toISOString(),
		};
		patchTask(selectedTask.id, (t) => ({
			...t,
			comments: [...t.comments, comment],
		}));
	};

	const changeStatus = (status: StageKey) => {
		if (!selectedTask) return;
		patchTask(selectedTask.id, (t) => ({ ...t, status }));
	};

	const createTask = (task: Task) => {
		setTasks((prev) => [task, ...prev]);
		setCreateOpen(false);
	};

	return (
		<div className="mx-auto max-w-[1440px] px-6 py-6">
			<TasksV2Header
				count={filtered.length}
				onNewTask={() => openCreate(null)}
			/>

			<TasksV2Filters
				filters={filters}
				onChange={setFilters}
				view={view}
				onViewChange={setView}
			/>

			{view === "board" ? (
				<KanbanBoard
					tasks={filtered}
					onOpenTask={(t) => setSelectedId(t.id)}
				/>
			) : (
				<TaskTable
					tasks={filtered}
					onOpenTask={(t) => setSelectedId(t.id)}
				/>
			)}

			<TaskDetailDrawer
				task={selectedTask}
				parentTask={parentTask}
				childTasks={childTasks}
				onClose={() => setSelectedId(null)}
				onToggleChecklist={toggleChecklist}
				onAddComment={addComment}
				onStatusChange={changeStatus}
				onOpenTask={(t) => setSelectedId(t.id)}
				onAddSubtask={() =>
					selectedTask && openCreate(selectedTask.id)
				}
				escEnabled={!createOpen}
			/>

			<DrawerShell
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				level="over"
			>
				{createOpen && (
					<CreateTaskDrawer
						parentTask={createParent}
						onClose={() => setCreateOpen(false)}
						onCreate={createTask}
					/>
				)}
			</DrawerShell>
		</div>
	);
}
